import { navigationGroups } from './navigationConfig';
import React, { ReactNode, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useRoute } from 'wouter';
import { Toaster } from '../../components/ui/sonner';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarProvider,
} from '../../components/ui/sidebar';
import { UserDropdown } from '../../components/user/UserDropdown';
import { useIsMobile } from '../../hooks/use-mobile';
import { useUser } from '../../hooks/use-user';
import { SidebarNavItem } from './SidebarNavItem';
import { ContextSwitcher } from '../contexts/ContextSwitcher';
import { useContextRouting } from '../../hooks/useContextRouting';
import { ContextRouteGuard } from '../contexts/ContextRouteGuard';
import { CategorySidebarGroup } from './CategorySidebarGroup';
import { SearchProvider } from '../search/SearchProvider';
import { GlobalCommandSearch } from '../search/GlobalCommandSearch';
import { FeatureErrorBoundary } from '../FeatureErrorBoundary';
import { MobileHeader } from './MobileHeader';

interface AppLayoutProps {
  children: ReactNode;
}


export const AppLayout: React.FC<AppLayoutProps> = React.memo(
  ({ children }) => {
    // Check if user is on mobile
    const isMobile = useIsMobile();
    // Get user information
    const { email, name } = useUser();
    // Get context routing info
    const { currentContextId } = useContextRouting();
    // Get categories for current context
    const categories = useQuery(
      api.categories.list,
      currentContextId ? { contextId: currentContextId } : 'skip'
    );
    
    // Check active routes for categories
    const [_, params] = useRoute('/context/:contextId/category/:categoryId/:rest*');
    const activeCategoryId = params?.categoryId;
    const [isManageCategoriesActive] = useRoute(`/context/${currentContextId}/categories/manage`);

    // Memoize header content to prevent re-renders  
    const mobileHeader = useMemo(
      () => (isMobile ? <MobileHeader /> : null),
      [isMobile]
    );

    // Memoize sidebar content
    const sidebarContent = useMemo(
      () => (
        <Sidebar variant="inset" collapsible="icon">
          <SidebarHeader>
            <ContextSwitcher />
          </SidebarHeader>
          
          <SidebarContent className="gap-1">
            {navigationGroups.map((group) => (
              <SidebarGroup key={group.label} className="pt-0 px-2 pb-2">
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => (
                      <SidebarNavItem item={item} key={item.href} />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}

            <CategorySidebarGroup
              categories={categories}
              currentContextId={currentContextId}
              activeCategoryId={activeCategoryId}
              isManageCategoriesActive={isManageCategoriesActive}
            />
          </SidebarContent>

          <SidebarFooter className="mt-auto border-t border-sidebar-border">
            <div className="p-2">
              <UserDropdown
                variant="sidebar"
                userEmail={email || undefined}
                userName={name || undefined}
              />
            </div>
          </SidebarFooter>
        </Sidebar>
      ),
      [email, name, categories, currentContextId, activeCategoryId, isManageCategoriesActive]
    );

    return (
      <FeatureErrorBoundary featureName="Search">
        <SearchProvider>
          <SidebarProvider>
            {/* Mobile-only top bar */}
            {mobileHeader}

            <SidebarInset className="w-full !m-0 !rounded-none">
              {/* Option 1: Using Resizable Panels with Inset Sidebar */}
              <div className={`flex h-screen ${isMobile ? 'pt-14' : ''}`}>
                {sidebarContent}

                <div className="flex h-full w-full flex-col">
                  <main className="h-full w-full overflow-auto p-4">
                    <ContextRouteGuard>
                      {children}
                    </ContextRouteGuard>
                  </main>
                </div>
              </div>
            </SidebarInset>
            <Toaster />
            <GlobalCommandSearch />
          </SidebarProvider>
        </SearchProvider>
      </FeatureErrorBoundary>
    );
  }
);

AppLayout.displayName = 'AppLayout';
